import type {
  MeResponse,
  RecordsResponse,
  RecordResponse,
  RecordInput,
  UserResponse,
} from "@shared/types";

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: any) {
    super(message);
    this.name = "ApiError";
  }
}

type AuthErrorListener = () => void;
const authErrorListeners: Set<AuthErrorListener> = new Set();

export function onAuthError(fn: AuthErrorListener): () => void {
  authErrorListeners.add(fn);
  return () => authErrorListeners.delete(fn);
}

async function req<T>(
  method: string,
  path: string,
  body?: unknown,
  ifMatch?: number
): Promise<T | null> {
  const headers: Record<string, string> = { "content-type": "application/json" };
  if (ifMatch != null) headers["If-Match"] = String(ifMatch);

  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      credentials: "same-origin",
    });
  } catch (e) {
    throw new ApiError(0, e instanceof Error ? e.message : "Network error");
  }

  if (res.status === 401 && path !== "/api/login") {
    authErrorListeners.forEach((fn) => fn());
    let data: any = {};
    try { data = await res.json(); } catch {}
    throw new ApiError(401, data.error || "unauthorized", data);
  }
  if (res.status === 204) return null;
  let data: any = {};
  try { data = await res.json(); } catch {}
  if (!res.ok) {
    throw new ApiError(res.status, data.error || res.statusText, data);
  }
  return data as T;
}

export const api = {
  login: (username: string, password: string) =>
    req<void>("POST", "/api/login", { username, password }),
  logout: () => req<void>("POST", "/api/logout"),
  me: () => req<MeResponse>("GET", "/api/me"),
  changePassword: (current: string | undefined, newPw: string) =>
    req<void>("POST", "/api/change-password", current ? { current, new: newPw } : { new: newPw }),
  records: (ids?: number[]) =>
    req<RecordsResponse>("GET", `/api/records${ids ? `?ids=${ids.join(",")}` : ""}`),
  createRecord: (r: RecordInput) =>
    req<RecordResponse>("POST", "/api/records", r),
  updateRecord: (id: number, r: RecordInput, version: number) =>
    req<RecordResponse>("PUT", `/api/records/${id}`, r, version),
  deleteRecord: (id: number, version: number) =>
    req<void>("DELETE", `/api/records/${id}`, undefined, version),
  addHistory: (id: number, txt: string, version: number) =>
    req<RecordResponse>("POST", `/api/records/${id}/history`, { txt }, version),
  addDoc: (id: number, nome: string, url: string, version: number) =>
    req<RecordResponse>("POST", `/api/records/${id}/docs`, { nome, url }, version),
  deleteDoc: (id: number, docId: number, version: number) =>
    req<RecordResponse>("DELETE", `/api/records/${id}/docs/${docId}`, undefined, version),
  importRecords: (records: unknown[]) =>
    req<{ imported: number; skipped: number }>("POST", "/api/records/import", { records }),
  dismissImport: () => req<void>("POST", "/api/records/dismiss-import"),
  listUsers: () => req<{ users: UserResponse[] }>("GET", "/api/users"),
  createUser: (username: string, display_name: string, is_admin: boolean) =>
    req<{ user: UserResponse; temp_password: string }>("POST", "/api/users", { username, display_name, is_admin }),
  patchUser: (id: number, patch: { is_active?: boolean; is_admin?: boolean; reset_password?: boolean; display_name?: string }) =>
    req<{ user: UserResponse; temp_password?: string }>("PATCH", `/api/users/${id}`, patch),
};
